import type { ComponentProps } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { mediaSrc } from '../lib/media';
import { ensureAccess } from '../lib/token-store';

/**
 * Session-gated media that survives an expired access token — the admin twin of
 * `apps/web-bidder/src/components/MediaImg.tsx`
 * ([ADR 0027](../../../../docs/adr/0027-login-gated-auction-surface.md)).
 *
 * A browser cannot put a header on an `<img src>`, so the access token rides the query string,
 * where it is a **snapshot** frozen into the URL at render time. It lives ~15 minutes and only
 * rotates when `ensureAccess()` runs, which happens only when a REST request goes out — and the
 * vehicle list, the media manager and the submissions queue do not poll. So an admin page left
 * open outlives its token, and anything loading after that moment (a lazy thumbnail scrolled into
 * view, a photo opened in the viewer) fetches with a dead token and renders broken while the
 * refresh token still represents a valid session.
 *
 * **This is largely older than ADR 0027**: the admin app's photos were already gated, because most
 * admin vehicles are not on a public-facing lot. What the ADR changed is the remainder — a
 * `show_public` photo on a public lot used to fall back to anonymous access, so it survived an
 * expired token. That fallback is gone, which takes the admin app from "most thumbnails break" to
 * "all of them do" and is why the fix lands here now.
 *
 * **Deliberately a copy, not a shared component.** The two apps have separate token stores and
 * separate `mediaSrc` helpers, so a version in `packages/ui` could not import either — it would
 * need the accessors injected through a provider at both app roots. That is a real abstraction to
 * carry for two consumers, and `packages/ui` is documented as tokens, fonts, brand assets and
 * shadcn-style primitives (ADR 0024) rather than app auth plumbing. If a third consumer appears,
 * extract it then. Keep the two in step: the retry semantics below are load-bearing, not cosmetic.
 *
 * **One retry per failure, not one per path for all time.** `error` also fires for a genuinely
 * missing object (a 404 from a row whose R2 bytes are gone), and an unconditional retry there is
 * an infinite reload loop. So the guard is spent on failure and re-armed by a **successful load**,
 * and it is banked only when a refresh actually returned a token — `refreshAccess` resolves `null`
 * for a transient 429/5xx or transport failure and keeps the session on purpose, so spending the
 * guard there would burn this path's one chance on an unrelated outage. Nothing here re-requests
 * the image, so after such a failure the element recovers on the next load attempt (a remount, or
 * a path change) rather than spontaneously.
 */
export function useMediaSrc(path: string | null | undefined): {
  src: string | undefined;
  onError: () => void;
  onLoad: () => void;
} {
  const [attempt, setAttempt] = useState(0);
  const retriedFor = useRef<string | null>(null);

  // `attempt` is in the dep list on purpose and unused in the body: bumping it re-runs `mediaSrc`,
  // which reads the *current* token out of the store. Without it the memo would keep handing back
  // the URL built from the expired one.
  const src = useMemo(() => mediaSrc(path), [path, attempt]);

  const onError = useCallback(() => {
    if (!path || retriedFor.current === path) return;
    void ensureAccess().then((token) => {
      if (!token) return;
      retriedFor.current = path;
      setAttempt((a) => a + 1);
    });
  }, [path]);

  const onLoad = useCallback(() => {
    retriedFor.current = null;
  }, []);

  return { src, onError, onLoad };
}

/**
 * An `<img>` for a session-gated media path. Exists so a list of images can each own a retry: a
 * hook cannot be called from inside a `.map()` whose length changes between renders — and a
 * component *can* be rendered conditionally, which is why the grids and the viewer use these
 * rather than calling the hook themselves.
 *
 * Renders nothing without a path; callers that want a placeholder decide what it looks like.
 */
export function MediaImg({
  path,
  ...rest
}: { path: string | null | undefined } & Omit<ComponentProps<'img'>, 'src' | 'onError'>) {
  const { src, onError, onLoad } = useMediaSrc(path);
  if (!src) return null;
  return <img src={src} onError={onError} onLoad={onLoad} {...rest} />;
}

/**
 * The same for a `<video>` — two of the four `MEDIA_KINDS` are moving pictures, and an `<img>`
 * pointed at an mp4 is a broken-image icon rather than a fallback.
 *
 * A `<video>` never fires `load` (its readiness events are `loadeddata` and friends), so its guard
 * stays spent after one recovery; a video needing a second heals on the next mount. Wiring
 * `onLoadedData` for that is more machinery than the case earns.
 */
export function MediaVideo({
  path,
  ...rest
}: { path: string | null | undefined } & Omit<ComponentProps<'video'>, 'src' | 'onError'>) {
  const { src, onError } = useMediaSrc(path);
  if (!src) return null;
  return <video src={src} onError={onError} {...rest} />;
}
